import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListProspectiveLearners,
  adminUpdateProspectiveLearner,
  adminInviteProspectiveLearner,
  adminListFoundingPartners,
  adminUpdateFoundingPartner,
  adminGetLearnerAuditLog,
  adminGetPartnerAttachmentUrl,
  adminListInviteClicks,
  LEARNER_STATUS_OPTIONS,
  PARTNER_STATUS_OPTIONS,
} from "@/lib/api/founding-partners.functions";
import { NDLCS_SKILLS_COURSES } from "@/lib/ndlcs-skills-courses";
import { toast } from "sonner";
import { Users, Building2, Search, Loader2, Save, History, Paperclip, UserPlus, X, MousePointerClick } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/prospective-learners",
)({
  component: ProspectiveLearnersPage,
});

type Tab = "learners" | "partners";

function ProspectiveLearnersPage() {
  const [tab, setTab] = useState<Tab>("learners");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-navy">Prospects & partners</h1>
        <p className="text-sm text-muted-foreground">
          Review and update the status of prospective learners and founding partner enquiries.
        </p>
      </div>
      <div className="flex gap-2 border-b border-border">
        <TabButton active={tab === "learners"} onClick={() => setTab("learners")} icon={<Users className="h-4 w-4" />}>
          Prospective learners
        </TabButton>
        <TabButton active={tab === "partners"} onClick={() => setTab("partners")} icon={<Building2 className="h-4 w-4" />}>
          Founding partners
        </TabButton>
      </div>
      {tab === "learners" ? (
        <>
          <LearnersPanel />
          <InviteClicksPanel />
        </>
      ) : (
        <PartnersPanel />
      )}
    </div>
  );
}

function TabButton({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition ${
        active ? "border-navy text-navy" : "border-transparent text-muted-foreground hover:text-navy"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ---------- LEARNERS ----------

function LearnersPanel() {
  const list = useServerFn(adminListProspectiveLearners);
  const update = useServerFn(adminUpdateProspectiveLearner);
  const invite = useServerFn(adminInviteProspectiveLearner);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [program, setProgram] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-learners", search, status, program],
    queryFn: () =>
      list({
        data: {
          search: search || undefined,
          status: (status || undefined) as any,
          program: program || undefined,
        } as never,
      }),
  });

  const mut = useMutation({
    mutationFn: (payload: {
      id: string;
      status?: string;
      notes?: string | null;
      partner_email?: string | null;
      partner_organisation?: string | null;
    }) => update({ data: payload as never }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-learners"] }),
  });

  const inviteMut = useMutation({
    mutationFn: (payload: {
      id: string;
      mark_enrolled?: boolean;
      course_slug?: string | null;
      course_title?: string | null;
      course_url?: string | null;
      course_note?: string | null;
    }) => invite({ data: payload as never }),
    onSuccess: (r) => {
      toast.success(
        r.created
          ? "Account created & welcome email sent."
          : "Learner already had an account — welcome email re-sent.",
      );
      qc.invalidateQueries({ queryKey: ["admin-learners"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Invite failed"),
  });

  // Learner currently being invited (drives the course-picker modal).
  const [invitingRow, setInvitingRow] = useState<any | null>(null);

  const rows = data?.rows ?? [];
  const programs = data?.programs ?? [];

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows as any[]) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, source…"
            className="rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {LEARNER_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={program} onChange={(e) => setProgram(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="">All programs</option>
          {programs.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="ml-auto text-xs text-muted-foreground">
          {rows.length} learners
          {Object.keys(counts).length ? " · " + Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(" · ") : ""}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Programs</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No learners match the filters.</td></tr>
            ) : (
              (rows as any[]).map((r) => (
                <LearnerRow
                  key={r.id}
                  row={r}
                  onSave={(patch) => mut.mutate({ id: r.id, ...patch })}
                  pending={mut.isPending}
                  onInvite={() => {
                    if (!r.email) { toast.error("No email on file"); return; }
                    setInvitingRow(r);
                  }}
                  inviting={inviteMut.isPending && invitingRow?.id === r.id}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {invitingRow ? (
        <InviteCourseModal
          row={invitingRow}
          submitting={inviteMut.isPending}
          onClose={() => setInvitingRow(null)}
          onConfirm={(payload: InvitePayload) => {
            inviteMut.mutate(
              { id: invitingRow.id, ...payload },
              { onSuccess: () => setInvitingRow(null) },
            );
          }}
        />
      ) : null}
    </div>
  );
}

type InvitePayload = {
  mark_enrolled?: boolean;
  course_slug?: string | null;
  course_title?: string | null;
  course_url?: string | null;
  course_note?: string | null;
};

function LearnerRow({
  row, onSave, pending, onInvite, inviting,
}: {
  row: any;
  onSave: (p: {
    status?: string;
    notes?: string | null;
    partner_email?: string | null;
    partner_organisation?: string | null;
  }) => void;
  pending: boolean;
  onInvite: () => void;
  inviting: boolean;
}) {
  const [status, setStatus] = useState<string>(row.status ?? "new");
  const [notes, setNotes] = useState<string>(row.notes ?? "");
  const [partnerEmail, setPartnerEmail] = useState<string>(row.partner_email ?? "");
  const [partnerOrg, setPartnerOrg] = useState<string>(row.partner_organisation ?? "");
  const [showAudit, setShowAudit] = useState(false);
  const getAudit = useServerFn(adminGetLearnerAuditLog);
  const auditQ = useQuery({
    queryKey: ["learner-audit", row.id],
    queryFn: () => getAudit({ data: { learner_id: row.id } as never }),
    enabled: showAudit,
  });
  const dirty =
    status !== (row.status ?? "new") ||
    notes !== (row.notes ?? "") ||
    partnerEmail !== (row.partner_email ?? "") ||
    partnerOrg !== (row.partner_organisation ?? "");
  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-2">
        <div className="font-medium text-navy">{row.first_name} {row.surname}</div>
        {row.id_number ? <div className="text-xs text-muted-foreground">ID {row.id_number}</div> : null}
      </td>
      <td className="px-3 py-2">
        <div>{row.email}</div>
        {row.cell_number ? <div className="text-xs text-muted-foreground">{row.cell_number}</div> : null}
      </td>
      <td className="px-3 py-2 text-xs">{(row.programs ?? []).join(", ") || "—"}</td>
      <td className="px-3 py-2 text-xs">{row.source ?? "—"}</td>
      <td className="px-3 py-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs">
          {LEARNER_STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <div className="mt-2 space-y-1">
          <input
            type="email"
            placeholder="Partner email (for status alerts)"
            value={partnerEmail}
            onChange={(e) => setPartnerEmail(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
          />
          <input
            type="text"
            placeholder="Partner organisation"
            value={partnerOrg}
            onChange={(e) => setPartnerOrg(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
          />
        </div>
      </td>
      <td className="px-3 py-2">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="min-w-[220px] rounded-md border border-input bg-background px-2 py-1 text-xs"
        />
        <div className="mt-1 flex flex-wrap gap-1">
          <button
            type="button"
            disabled={!dirty || pending}
            onClick={() =>
              onSave({
                status,
                notes: notes || null,
                partner_email: partnerEmail || null,
                partner_organisation: partnerOrg || null,
              })
            }
            className="inline-flex items-center gap-1 rounded-md bg-navy px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
          >
            <Save className="h-3 w-3" /> Save
          </button>
          <button
            type="button"
            onClick={() => setShowAudit((v) => !v)}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs"
          >
            <History className="h-3 w-3" /> {showAudit ? "Hide" : "Audit"}
          </button>
          <button
            type="button"
            disabled={inviting || !row.email}
            onClick={() => onInvite()}
            title="Create a user account (if missing) and email login details"
            className="inline-flex items-center gap-1 rounded-md border border-navy/30 bg-navy/5 px-2 py-1 text-xs font-semibold text-navy disabled:opacity-40"
          >
            <UserPlus className="h-3 w-3" /> {inviting ? "Inviting…" : row.onboarded_user_id ? "Resend welcome" : "Invite & create account"}
          </button>
        </div>
        {showAudit ? (
          <div className="mt-2 rounded-md border border-border bg-muted/40 p-2 text-xs">
            {auditQ.isLoading ? (
              <div className="text-muted-foreground">Loading history…</div>
            ) : (auditQ.data?.rows ?? []).length === 0 ? (
              <div className="text-muted-foreground">No changes recorded yet.</div>
            ) : (
              <ul className="space-y-1">
                {(auditQ.data!.rows as any[]).map((a) => (
                  <li key={a.id} className="border-b border-border/60 pb-1 last:border-b-0">
                    <div className="font-medium text-navy">
                      {a.field}: {String(a.old_value ?? "—")} → {String(a.new_value ?? "—")}
                    </div>
                    <div className="text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()} · {a.actor_email ?? a.actor_id ?? "system"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

// ---------- PARTNERS ----------

function PartnersPanel() {
  const list = useServerFn(adminListFoundingPartners);
  const update = useServerFn(adminUpdateFoundingPartner);
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-partners", search, status],
    queryFn: () =>
      list({
        data: {
          search: search || undefined,
          status: (status || undefined) as any,
        } as never,
      }),
  });
  const mut = useMutation({
    mutationFn: (payload: { id: string; status?: string; admin_notes?: string | null }) =>
      update({ data: payload as never }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-partners"] }),
  });

  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organisation, contact…"
            className="rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {PARTNER_STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <div className="ml-auto text-xs text-muted-foreground">{rows.length} enquiries</div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Type / Tier</th>
              <th className="px-3 py-2">Interests</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Admin notes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No partner enquiries yet.</td></tr>
            ) : (
              (rows as any[]).map((r) => (
                <PartnerRow key={r.id} row={r} onSave={(patch) => mut.mutate({ id: r.id, ...patch })} pending={mut.isPending} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PartnerRow({
  row, onSave, pending,
}: { row: any; onSave: (p: { status?: string; admin_notes?: string | null }) => void; pending: boolean }) {
  const [status, setStatus] = useState<string>(row.status ?? "new");
  const [notes, setNotes] = useState<string>(row.admin_notes ?? "");
  const dirty = status !== (row.status ?? "new") || notes !== (row.admin_notes ?? "");
  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-2">
        <div className="font-medium text-navy">{row.organisation}</div>
        <div className="text-xs text-muted-foreground">
          {[row.city, row.country].filter(Boolean).join(", ") || "—"}
          {row.website ? <> · <a href={row.website.startsWith("http") ? row.website : `https://${row.website}`} target="_blank" rel="noreferrer" className="underline">web</a></> : null}
        </div>
      </td>
      <td className="px-3 py-2">
        <div>{row.contact_name}</div>
        <div className="text-xs text-muted-foreground">{row.email}{row.phone ? ` · ${row.phone}` : ""}</div>
        {row.role ? <div className="text-xs text-muted-foreground">{row.role}</div> : null}
      </td>
      <td className="px-3 py-2 text-xs">
        <div>{row.partner_type}</div>
        <div className="text-muted-foreground">{row.partnership_tier ?? "—"}</div>
        {row.team_size ? <div className="text-muted-foreground">Team: {row.team_size}</div> : null}
        {row.annual_hiring_target ? <div className="text-muted-foreground">Hiring: {row.annual_hiring_target}</div> : null}
      </td>
      <td className="px-3 py-2 text-xs">{(row.interests ?? []).join(", ") || "—"}</td>
      <td className="px-3 py-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs">
          {PARTNER_STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </td>
      <td className="px-3 py-2">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="min-w-[220px] rounded-md border border-input bg-background px-2 py-1 text-xs"
        />
        {row.message ? (
          <details className="mt-1 text-xs text-muted-foreground">
            <summary className="cursor-pointer">Submitted message</summary>
            <p className="mt-1 whitespace-pre-wrap">{row.message}</p>
          </details>
        ) : null}
        {Array.isArray(row.attachments) && row.attachments.length > 0 ? (
          <PartnerAttachmentsList attachments={row.attachments} />
        ) : null}
        <button
          type="button"
          disabled={!dirty || pending}
          onClick={() => onSave({ status, admin_notes: notes || null })}
          className="mt-1 inline-flex items-center gap-1 rounded-md bg-navy px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
        >
          <Save className="h-3 w-3" /> Save
        </button>
      </td>
    </tr>
  );
}

function PartnerAttachmentsList({ attachments }: { attachments: any[] }) {
  const getUrl = useServerFn(adminGetPartnerAttachmentUrl);
  const [busy, setBusy] = useState<string | null>(null);
  async function open(path: string) {
    setBusy(path);
    try {
      const r = await getUrl({ data: { path } as never });
      window.open(r.url, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="mt-2 space-y-1 text-xs">
      <div className="flex items-center gap-1 font-semibold text-navy">
        <Paperclip className="h-3 w-3" /> Attachments ({attachments.length})
      </div>
      <ul className="space-y-1">
        {attachments.map((a) => (
          <li key={a.path} className="flex items-center justify-between gap-2">
            <span className="truncate">
              {a.name} <span className="text-muted-foreground">· {Math.round((a.size ?? 0) / 1024)} KB</span>
            </span>
            <button
              type="button"
              onClick={() => open(a.path)}
              disabled={busy === a.path}
              className="rounded-md border border-input px-2 py-0.5 text-xs disabled:opacity-40"
            >
              {busy === a.path ? "Opening…" : "Open"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Course-picker modal shown when the admin clicks "Invite & create account".
// Lets you pick one of the in-house NDLCS skills courses for this learner's
// internship placement (or enter a custom one) before the welcome email goes
// out. The chosen course is merged into the learner's `programs` array and
// surfaced in the welcome email so the learner knows exactly what they've
// been placed into.
function InviteCourseModal({
  row,
  submitting,
  onClose,
  onConfirm,
}: {
  row: any;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (payload: InvitePayload) => void;
}) {
  const [slug, setSlug] = useState<string>("");
  const [customTitle, setCustomTitle] = useState("");
  const [note, setNote] = useState("");
  const [markEnrolled, setMarkEnrolled] = useState(false);
  const [isInternship, setIsInternship] = useState(false);

  const picked = NDLCS_SKILLS_COURSES.find((c) => c.slug === slug);
  const learnerName =
    [row.first_name, row.surname].filter(Boolean).join(" ").trim() || row.email;

  function submit() {
    let course_slug: string | null = null;
    let course_title: string | null = null;
    let course_url: string | null = null;

    if (slug === "__custom") {
      course_title = customTitle.trim() || null;
      course_slug = course_title ? "custom" : null;
    } else if (picked) {
      course_slug = picked.slug;
      course_title = `${picked.code} — ${picked.title}`;
      course_url = `https://nkadimengdannylcs.com/education/skills/${picked.slug}`;
    }

    // Internship placement always lands in SA LexPractiq — that's where the
    // simulations, mentor sessions and portfolio evidence live.
    if (isInternship) {
      course_url = "https://nkadimengdannylcs.com/lexpractiq";
      if (!course_title) {
        course_title = "SA LexPractiq — Legal Experience Internship";
        course_slug = course_slug ?? "lexpractiq-internship";
      } else {
        course_title = `${course_title} · SA LexPractiq Internship`;
      }
    }

    onConfirm({
      mark_enrolled: markEnrolled,
      course_slug,
      course_title,
      course_url,
      course_note: note.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
      <div className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-navy">Invite learner &amp; assign internship course</div>
            <div className="truncate text-xs text-muted-foreground">
              {learnerName} · {row.email}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <div>
            <label className="mb-1 block text-xs font-semibold text-navy">Internship course</label>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— No course (welcome only) —</option>
              {NDLCS_SKILLS_COURSES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.code} — {c.title} ({c.category})
                </option>
              ))}
              <option value="__custom">Custom course…</option>
            </select>
          </div>

          {slug === "__custom" ? (
            <div>
              <label className="mb-1 block text-xs font-semibold text-navy">Custom course title</label>
              <input
                type="text"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="e.g. Legal Research Internship — Cohort 4"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          ) : null}

          <div>
            <label className="mb-1 block text-xs font-semibold text-navy">Message to include in the welcome email (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Your induction is on Monday 10:00. Please review the intake checklist beforehand."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={markEnrolled}
              onChange={(e) => setMarkEnrolled(e.target.checked)}
            />
            Mark this learner as <strong>enrolled</strong> (otherwise sets status to <strong>invited</strong>).
          </label>

          <label className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900">
            <input
              type="checkbox"
              checked={isInternship}
              onChange={(e) => setIsInternship(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              This is an <strong>SA LexPractiq internship placement</strong> — the
              welcome email link will direct the learner to <code>/lexpractiq</code>
              instead of the NDLCS Academy course page.
            </span>
          </label>

          <p className="rounded-md border border-border bg-muted/40 p-2 text-xs text-muted-foreground">
            An account will be created if one doesn't exist, a temporary password will be emailed
            to the learner, and a copy of that email will also be delivered to your admin inbox.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || (slug === "__custom" && !customTitle.trim())}
            className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
            {submitting ? "Sending invite…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}


// Placement / course link click tracking. Populated by the public
// /api/public/invite-click redirect that wraps every course URL we email out.
function InviteClicksPanel() {
  const fetchClicks = useServerFn(adminListInviteClicks);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-invite-clicks"],
    queryFn: () => fetchClicks(),
    refetchInterval: 60_000,
  });
  const clicks: any[] = data?.clicks ?? [];
  return (
    <section className="rounded-lg border border-input bg-card p-4">
      <header className="mb-3 flex items-center gap-2">
        <MousePointerClick className="h-4 w-4 text-navy" />
        <h2 className="font-display text-lg text-navy">Placement link clicks</h2>
        <span className="ml-2 text-xs text-muted-foreground">
          Learners who opened the course or SA LexPractiq link from their welcome email.
        </span>
      </header>
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : clicks.length === 0 ? (
        <div className="text-sm text-muted-foreground">No clicks recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Learner</th>
                <th className="px-2 py-2">Destination</th>
                <th className="px-2 py-2">Kind</th>
                <th className="px-2 py-2">Course</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {clicks.map((c) => (
                <tr key={c.id}>
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    {new Date(c.clicked_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-xs">{c.learner_email ?? "—"}</td>
                  <td className="px-2 py-2 text-xs">
                    <a href={c.destination} target="_blank" rel="noreferrer" className="text-navy underline">
                      {c.destination.replace(/^https?:\/\//, "").slice(0, 60)}
                    </a>
                  </td>
                  <td className="px-2 py-2 text-xs">
                    <span className={`rounded px-2 py-0.5 ${c.destination_kind === "lexpractiq" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {c.destination_kind}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs">{c.course_slug ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
